import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../../../providers/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getWidgetById } from '../../../../../services/apiWidgests';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
  CartesianGrid,
  Legend,
} from 'recharts';
import { useDashboards } from '../../../../../providers/DashboardContext';
import Spinner from '../../../../../components/Spinner';
import DatabaseOffIco from '../../../../../components/icons/DatabaseOffIco';
import useWidgetRuntime from '../../../../../hooks/useWidgetRuntime';
import { useAppContext } from '../../../../../providers/AppContext';

// 🎨 Generador de colores automáticos según paleta
const generatePaletteColor = (palette, index, total) => {
  const hueStep = 360 / total;
  let baseHue;
  switch (palette) {
    case 'red': baseHue = 0; break;
    case 'blue': baseHue = 210; break;
    case 'pastel':
    default: baseHue = (index * hueStep) % 360; break;
  }

  let saturation, lightness;
  if (palette === 'red') { saturation = 70; lightness = 55 - (index * 3) % 15; }
  else if (palette === 'blue') { saturation = 65; lightness = 50 - (index * 3) % 15; }
  else { saturation = 60; lightness = 70; }

  return `hsl(${baseHue + index * 10}, ${saturation}%, ${lightness}%)`;
};

// 🔅 Oscurecer un HSL
const darkenHSL = (hsl, amount = 15) => {
  const [h, s, l] = hsl.replace(/hsl|\(|\)|%/g, '').split(',').map(Number);
  return `hsl(${h}, ${s}%, ${Math.max(l - amount, 0)}%)`;
};

// 📈 Redondear máximo del eje
const getNiceMax = (max) => {
  if (max <= 10) return 10;
  if (max <= 100) return Math.ceil(max / 10) * 10;
  if (max <= 500) return Math.ceil(max / 50) * 50;
  return Math.ceil(max / 100) * 100;
};

const HorizontalBarChartWidget = ({ id }) => {
  const { addToast } = useAppContext();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { dashboard, selectedDatarange } = useDashboards();
  const { data: runtimeData, loading: fetchingWidgetData } = useWidgetRuntime(id, dashboard?._id, undefined, JSON.stringify(selectedDatarange));

  const [data, setData] = useState([]);
  const [dataW, setDataW] = useState([]);
  const [config, setConfig] = useState(null);
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [horizontalLabel, setHorizontalLabel] = useState('');
  const [verticalLabel, setVerticalLabel] = useState('');
  const [horizontalType, setHorizontalType] = useState('numeric');
  const [fixedKeys, setFixedKeys] = useState([]);

  // --- Info del widget ---
  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      const configuration = response?.configuration || {};
      setConfig(configuration);
      const hAxis = configuration?.horizontalAxis || {};
      const vAxis = configuration?.verticalAxis || {};
      setHorizontalLabel(hAxis.alias || hAxis.key || '');
      setVerticalLabel(vAxis.alias || vAxis.key || '');
      setHorizontalType(hAxis.type || 'numeric');
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      if (error.response?.status === 404) console.log('Widget no encontrado');
      else if (error.response?.status === 500) console.log('Error servidor');
      else navigate('/logout');
    } finally {
      setFetchingWidgetInfo(false);
    }
  };

  useEffect(() => {
    refreshWidgetInfo();
  }, [selectedDatarange]);

  // Los datos llegan por socket desde el singleton compartido del backend.
  useEffect(() => {
    setData(runtimeData?.records || []);
  }, [runtimeData]);

  // --- Preparar data con colores y orden fijo ---
  useEffect(() => {
    if (!config || !data || data.length === 0) {
      setDataW([]);
      return;
    }

    const { colorPalette = 'pastel', fixedColor } = config;

    const filteredData = data.filter(item =>
      Object.entries(item).some(
        ([key, val]) => !['label', 'color', 'borderColor'].includes(key) && typeof val === 'number' && val > 0
      )
    );

    const keys = Array.from(
      new Set(
        filteredData.flatMap(d =>
          Object.keys(d).filter(k => !['label', 'color', 'borderColor'].includes(k))
        )
      )
    );

    if (fixedKeys.length === 0) setFixedKeys(keys.sort()); // fijar orden al primer render

    const coloredData = filteredData.map((item) => {
      const newItem = { ...item };
      fixedKeys.forEach((key, idx) => {
        const baseColor =
          colorPalette === 'fixed'
            ? fixedColor || '#4B5563'
            : generatePaletteColor(colorPalette, idx, fixedKeys.length);
        if (newItem[key] != null) {
          newItem.color = baseColor;
          newItem.borderColor = darkenHSL(baseColor, 20);
        }
      });
      return newItem;
    });

    setDataW(coloredData);
  }, [config, data, fixedKeys]);

  if (!config || fetchingWidgetInfo || fetchingWidgetData) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  if (!dataW || dataW.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <span className="text-lg text-gray-700">No hay datos por el momento</span>
        <DatabaseOffIco width="40px" height="40px" fill="gray" />
      </div>
    );
  }

  const { colorPalette = 'pastel', fixedColor, textColor = '#111827' } = config;

  const maxValue = Math.max(
    ...dataW.map(d =>
      fixedKeys.length > 1 ? fixedKeys.reduce((sum, k) => sum + (d[k] || 0), 0) : d.value || 0
    )
  );

  const axisMax = getNiceMax(maxValue) * 1.1;

  const formatValue = (value) => {
    if (horizontalType === 'time' && typeof value === 'number') {
      const totalSeconds = Math.round(value);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      return [h, m, s].map(v => v.toString().padStart(2, '0')).join(':');
    }
    return value.toString();
  };

  const isGrouped = fixedKeys.length > 1;

  return (
    <div className="w-full h-full">
      <div className="text-center border border-gray-300">
        <span className="font-bold text-gray-600">{horizontalLabel} vs {verticalLabel}</span>
      </div>

      <ResponsiveContainer width="100%" height="90%">
        <BarChart
          layout={isGrouped ? 'horizontal' : 'vertical'}
          data={dataW}
          isAnimationActive={false}
          margin={{ top: 20, right: 30, left: 60, bottom: 10 }}
        >
          <XAxis
            {...(isGrouped
              ? { type: 'category', dataKey: 'label' }
              : { type: 'number', domain: [0, axisMax], tickFormatter: formatValue })}
            tick={{ fill: '#6b7280', fontSize: 14 }}
          />
          <YAxis
            {...(isGrouped
              ? { type: 'number', domain: [0, axisMax], tickFormatter: formatValue }
              : { type: 'category', dataKey: 'label' })}
            tick={{ fill: '#6b7280', fontSize: 14 }}
          />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <div className="bg-gray-800 text-white text-sm rounded px-3 py-2 shadow-md">
                  <div className="font-semibold mb-1">{label}</div>
                  {payload.map((entry, i) => (
                    <div key={i} className="flex items-center space-x-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.fill || entry.payload.color }} />
                      <span>{`${entry.name}: ${formatValue(entry.value)}`}</span>
                    </div>
                  ))}
                </div>
              ) : null
            }
          />
          {isGrouped ? (
            fixedKeys.map((key, i) => {
              const fill = colorPalette === 'fixed' ? fixedColor || '#4B5563' : generatePaletteColor(colorPalette, i, fixedKeys.length);
              const stroke = darkenHSL(fill, 20);
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={1}
                  name={key}
                  minPointSize={4}
                  isAnimationActive={false}
                >
                  <LabelList dataKey={key} position="inside" formatter={formatValue} style={{ fill: textColor, fontWeight: 'bold', fontSize: 12 }} />
                </Bar>
              );
            })
          ) : (
            <Bar dataKey="value" name="Valor" minPointSize={4}>
              {dataW.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.borderColor} strokeWidth={1} />
              ))}
              <LabelList dataKey="value" position="right" formatter={formatValue} style={{ fill: textColor, fontWeight: 'bold', fontSize: 12 }} />
            </Bar>
          )}
          {isGrouped && (
            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ paddingBottom: '10px', color: textColor, fontWeight: 'bold' }}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default HorizontalBarChartWidget;
